import { useAtomValue, useSetAtom } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'

import { surveyGroupFilterAtom } from '~/atoms'
import { graphql } from '~/gql'
import { useCurrentSurveyGroup } from '~/hooks/useCurrentSurveyGroup'
import { useTabulate } from '~/hooks/useTabulate'
import { exists, toSurveyGroupFilterItemInputGroups } from '~/utils'

import {
  commentAtom,
  isPendingTabulationAtom,
  isTabulationReadyAtom,
  savedCommentAtom,
  tabulationQueryDataAtom,
} from '../atoms'

const brandMutationDocument = graphql(`
  mutation TimeSeriesAnalysisPage_BrandTabulationMutation(
    $surveyGroupId: String!
    $filter: [[SurveyGroupCustomerAttributeFilterItemInput!]!]!
  ) {
    count: countSurveyGroupBrand(
      surveyGroupId: $surveyGroupId
      customFilter: { filter: $filter }
    ) {
      awaitId
    }
  }
`)

const brandQueryDocument = graphql(`
  query TimeSeriesAnalysisPage_BrandTabulation($awaitId: String!) {
    count: countSurveyGroupBrand(awaitId: $awaitId) {
      arrived
      result {
        ...TimeSeriesAnalysisPage_BrandTabulationQueryData
      }
    }
  }
`)

const questionMutationDocument = graphql(`
  mutation TimeSeriesAnalysisPage_QuestionTabulationMutation(
    $surveyGroupId: String!
    $filter: [[SurveyGroupCustomerAttributeFilterItemInput!]!]!
  ) {
    count: countSurveyGroupQuestion(
      surveyGroupId: $surveyGroupId
      customFilter: { filter: $filter }
    ) {
      awaitId
    }
  }
`)

const questionQueryDocument = graphql(`
  query TimeSeriesAnalysisPage_QuestionTabulation($awaitId: String!) {
    count: countSurveyGroupQuestion(awaitId: $awaitId) {
      arrived
      result {
        ...TimeSeriesAnalysisPage_QuestionTabulationQueryData
      }
    }
  }
`)

const customerAttributeMutationDocument = graphql(`
  mutation TimeSeriesAnalysisPage_CustomerAttributeTabulationMutation(
    $surveyGroupId: String!
    $filter: [[SurveyGroupCustomerAttributeFilterItemInput!]!]!
  ) {
    count: countSurveyGroupCustomerAttributes(
      surveyGroupId: $surveyGroupId
      customFilter: { filter: $filter }
    ) {
      awaitId
    }
  }
`)

const customerAttributeQueryDocument = graphql(`
  query TimeSeriesAnalysisPage_CustomerAttributeTabulation($awaitId: String!) {
    count: countSurveyGroupCustomerAttributes(awaitId: $awaitId) {
      arrived
      result {
        ...TimeSeriesAnalysisPage_CustomerAttributeTabulationQueryData
      }
    }
  }
`)

export const useTabulationQuery = () => {
  const isTabulationReady = useAtomValue(isTabulationReadyAtom)
  const isPendingTabulation = useAtomValue(isPendingTabulationAtom)
  const surveyGroup = useCurrentSurveyGroup()
  const surveyGroupFilter = useAtomValue(surveyGroupFilterAtom)

  const [tabulateBrandState, tabulateBrand] = useTabulate(
    brandMutationDocument,
    brandQueryDocument,
  )
  const [tabulateQuestionState, tabulateQuestion] = useTabulate(
    questionMutationDocument,
    questionQueryDocument,
  )
  const [tabulateCustomerAttributeState, tabulateCustomerAttribute] =
    useTabulate(
      customerAttributeMutationDocument,
      customerAttributeQueryDocument,
    )

  const setTabulationQueryData = useSetAtom(tabulationQueryDataAtom)
  const resetComment = useResetAtom(commentAtom)
  const resetSavedComment = useResetAtom(savedCommentAtom)

  const hasTabulatedRef = React.useRef(false)

  const tabulateIfNeeded = React.useCallback(async () => {
    const surveyGroupId = surveyGroup?.id

    if (!isTabulationReady || isPendingTabulation || !exists(surveyGroupId)) {
      return
    }

    setTabulationQueryData(null)

    const filter = toSurveyGroupFilterItemInputGroups(
      surveyGroupFilter?.filterGroups ?? [],
    )

    const [brandQueryData, questionQueryData, customerAttributeQueryData] =
      await Promise.all([
        tabulateBrand({ surveyGroupId, filter }),
        tabulateQuestion({ surveyGroupId, filter }),
        tabulateCustomerAttribute({ surveyGroupId, filter }),
      ])

    setTabulationQueryData({
      brands: brandQueryData,
      questions: questionQueryData,
      customerAttributes: customerAttributeQueryData,
    })

    // 再集計時は古いコメントが新しいデータに残らないようリセットする。
    // 初回集計（URL 復元したコメントを保持）ではリセットしない。
    if (hasTabulatedRef.current) {
      resetComment()
      resetSavedComment()
    }
    hasTabulatedRef.current = true
  }, [
    isPendingTabulation,
    isTabulationReady,
    resetComment,
    resetSavedComment,
    setTabulationQueryData,
    surveyGroup?.id,
    surveyGroupFilter?.filterGroups,
    tabulateBrand,
    tabulateCustomerAttribute,
    tabulateQuestion,
  ])

  React.useEffect(() => {
    tabulateIfNeeded()
  }, [tabulateIfNeeded])

  return (
    tabulateBrandState.loading ||
    tabulateQuestionState.loading ||
    tabulateCustomerAttributeState.loading
  )
}
